import { useEffect, useRef, useState, type ReactNode, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Attachment, CannedResponse, Conversation, Message } from '../types';
import { api, ApiError } from '../lib/api';
import { formatTime } from '../lib/format';
import { IconBack, IconSend, MaterialSymbol } from './icons';

/**
 * ChatWindow — the center thread pane: header (customer + status actions),
 * message list (auto-scrolls, polls every 5s) and the reply composer.
 */

const POLL_INTERVAL_MS = 5000;

interface ChatWindowProps {
  conversationId: string;
  plan: 'FREE' | 'PRO';
  onConversationUpdate?: (conversation: Conversation) => void;
}

export default function ChatWindow({
  conversationId,
  plan,
  onConversationUpdate,
}: ChatWindowProps): ReactNode {
  const navigate = useNavigate();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [canned, setCanned] = useState<CannedResponse[]>([]);
  const [cannedOpen, setCannedOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [attachEnabled, setAttachEnabled] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Saved agent replies (macros) — loaded once; the picker button hides when
  // the store has none.
  useEffect(() => {
    let cancelled = false;
    api
      .listCannedResponses()
      .then(({ cannedResponses }) => {
        if (!cancelled) setCanned(cannedResponses);
      })
      .catch(() => {
        // Non-fatal — the composer just won't show the picker.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load attachment capability once — non-fatal if it fails.
  useEffect(() => {
    let cancelled = false;
    api
      .getSettings()
      .then((res) => {
        if (!cancelled) setAttachEnabled(Boolean(res.attachments?.enabled));
      })
      .catch(() => {
        /* non-fatal */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const insertCanned = (content: string): void => {
    setReply((prev) => (prev.trim() ? `${prev.trimEnd()} ${content}` : content));
    setCannedOpen(false);
    // Refocus the textarea and drop the caret at the end.
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    });
  };

  // Reset the composer when switching conversations so staged files/text
  // never carry over to the wrong thread.
  useEffect(() => {
    setPendingFiles([]);
    setReply('');
  }, [conversationId]);

  // Initial load + polling.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setConversation(null);

    const load = async (): Promise<void> => {
      try {
        const { conversation: c } = await api.getConversation(conversationId);
        if (cancelled) return;
        setConversation(c);
        onConversationUpdate?.(c);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load conversation');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    const interval = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [conversationId, onConversationUpdate]);

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [conversation?.messages.length]);

  // Tier gating: FREE stores get 3 total attachments across all messages.
  const usedAttachments =
    conversation?.messages.reduce((n, m) => n + (m.attachments?.length ?? 0), 0) ?? 0;
  const remaining = plan === 'FREE' ? Math.max(0, 3 - usedAttachments) : null;
  const canAttach = attachEnabled && remaining !== 0;

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const text = reply.trim();
    if ((!text && pendingFiles.length === 0) || sending || !conversation) return;
    setSending(true);
    try {
      const { message } =
        pendingFiles.length > 0
          ? await api.sendAdminMessageWithFiles(conversation.id, text, pendingFiles)
          : await api.sendAdminMessage(conversation.id, text);
      setConversation((prev) =>
        prev ? { ...prev, messages: [...prev.messages, message] } : prev,
      );
      setReply('');
      setPendingFiles([]);
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Send failed';
      window.shopify?.toast.show(msg, { isError: true });
    } finally {
      setSending(false);
    }
  };

  const toggleStatus = async (): Promise<void> => {
    if (!conversation || togglingStatus) return;
    const next = conversation.status === 'OPEN' ? 'CLOSED' : 'OPEN';
    setTogglingStatus(true);
    try {
      const { conversation: updated } = await api.setConversationStatus(conversation.id, next);
      setConversation((prev) => {
        if (!prev) return prev;
        const merged = { ...prev, status: updated.status };
        onConversationUpdate?.(merged);
        return merged;
      });
      window.shopify?.toast.show(`Conversation ${next.toLowerCase()}`);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to update status';
      window.shopify?.toast.show(msg, { isError: true });
    } finally {
      setTogglingStatus(false);
    }
  };

  if (loading) {
    return (
      <section className="cg-thread">
        <div className="cg-spinner" aria-label="Loading conversation" />
      </section>
    );
  }

  if (error || !conversation) {
    return (
      <section className="cg-thread">
        <div style={{ padding: 24 }}>
          <div className="cg-banner cg-banner-error">{error ?? 'Conversation not found'}</div>
        </div>
      </section>
    );
  }

  const isOpen = conversation.status === 'OPEN';

  return (
    <section className="cg-thread" aria-label={`Conversation with ${conversation.customerName}`}>
      <div className="cg-thread-header">
        <div className="cg-thread-headline">
          <button
            type="button"
            className="cg-btn-secondary"
            onClick={() => navigate('/conversations')}
            aria-label="Back to conversations"
            style={{ padding: 9 }}
          >
            <IconBack size={16} />
          </button>
          <div style={{ minWidth: 0 }}>
            <h2 className="cg-thread-name">{conversation.customerName}</h2>
            <div className="cg-thread-email">{conversation.customerEmail}</div>
          </div>
          <span className="cg-case-chip" title={conversation.id}>
            Case #{conversation.id.slice(0, 6).toUpperCase()}
          </span>
        </div>
        <div className="cg-thread-actions">
          <span className={`cg-chip ${isOpen ? 'cg-chip-open' : 'cg-chip-closed'}`}>
            {conversation.status}
          </span>
          <button
            type="button"
            className={isOpen ? 'cg-btn-urgent' : 'cg-btn-secondary'}
            onClick={() => void toggleStatus()}
            disabled={togglingStatus}
          >
            {isOpen ? 'Close chat' : 'Reopen'}
          </button>
        </div>
      </div>

      <div ref={listRef} className="cg-thread-messages">
        {conversation.messages.length === 0 ? (
          <div className="cg-empty">
            <div className="cg-empty-sub">No messages yet.</div>
          </div>
        ) : (
          conversation.messages.map((m) => <MessageBubble key={m.id} message={m} />)
        )}
      </div>

      <form className="cg-thread-footer" onSubmit={(e) => void onSubmit(e)}>
        {cannedOpen && canned.length > 0 ? (
          <>
            <div className="cg-canned-backdrop" onClick={() => setCannedOpen(false)} />
            <div className="cg-canned-popover" role="listbox" aria-label="Saved replies">
              <div className="cg-canned-popover-head">Saved replies</div>
              {canned.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="cg-canned-item"
                  onClick={() => insertCanned(c.content)}
                >
                  <span className="cg-canned-item-title">{c.title}</span>
                  <span className="cg-canned-item-preview">{c.content}</span>
                </button>
              ))}
            </div>
          </>
        ) : null}
        {pendingFiles.length > 0 ? (
          <div className="cg-attach-chips">
            {pendingFiles.map((f, i) => (
              <span key={i} className="cg-attach-chip">
                {f.name}
                <button
                  type="button"
                  aria-label={`Remove ${f.name}`}
                  onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : null}
        {attachEnabled && remaining === 0 ? (
          <p className="cg-attach-hint">Upgrade to Pro for unlimited attachments.</p>
        ) : null}
        <div className="cg-composer">
          {canAttach ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files) {
                    setPendingFiles((prev) => [...prev, ...Array.from(e.target.files as FileList)]);
                    e.target.value = '';
                  }
                }}
              />
              <button
                type="button"
                className="cg-composer-canned"
                aria-label="Attach a file"
                title="Attach a file"
                onClick={() => fileInputRef.current?.click()}
              >
                <MaterialSymbol name="attach_file" size={20} />
              </button>
            </>
          ) : null}
          {canned.length > 0 ? (
            <button
              type="button"
              className="cg-composer-canned"
              aria-label="Insert a saved reply"
              title="Saved replies"
              aria-expanded={cannedOpen}
              onClick={() => setCannedOpen((o) => !o)}
            >
              <MaterialSymbol name="bolt" size={20} />
            </button>
          ) : null}
          <textarea
            ref={textareaRef}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Type your reply…"
            rows={1}
            aria-label="Reply to customer"
            disabled={sending}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                e.currentTarget.form?.requestSubmit();
              }
            }}
          />
          <button
            type="submit"
            className="cg-composer-send"
            disabled={sending || (!reply.trim() && pendingFiles.length === 0)}
            aria-label="Send reply"
          >
            <IconSend />
          </button>
        </div>
      </form>
    </section>
  );
}

function MessageBubble({ message }: { message: Message }): ReactNode {
  // AI replies sit on the store side, marked so the merchant can tell them
  // apart from their own messages.
  const isStoreSide = message.sender !== 'CUSTOMER';
  return (
    <div className={`cg-msg-group ${isStoreSide ? 'cg-from-admin' : 'cg-from-customer'}`}>
      <div className="cg-msg">
        {message.content ? <span>{message.content}</span> : null}
        {message.attachments && message.attachments.length > 0 ? (
          <div className="cg-atts">
            {message.attachments.map((a: Attachment) =>
              a.isImage ? (
                <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer" className="cg-att-image">
                  <img src={a.url} alt={a.filename} loading="lazy" />
                </a>
              ) : (
                <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer" className="cg-att-file">
                  <MaterialSymbol name="description" size={18} />
                  <span className="cg-att-name">{a.filename}</span>
                </a>
              ),
            )}
          </div>
        ) : null}
      </div>
      <span className="cg-msg-time">
        {message.sender === 'AI' ? <span className="cg-ai-chip">AI</span> : null}
        {formatTime(message.createdAt)}
      </span>
    </div>
  );
}
